import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import LoginModal from '../../components/LoginModal';
import logo from '../../assets/new-logo.png';
import { checkInactivity } from '../../utils/sessionManager';

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(() => searchParams.get('login') === 'true');

  useEffect(() => {
    if (searchParams.get('login') === 'true') {
      setIsLoginModalOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const accountType = localStorage.getItem('account_type');
    if (accessToken && !checkInactivity(false)) {
      if (accountType === 'agency') {
        navigate('/agency-dashboard');
      } else {
        navigate('/marketing-intricacies');
      }
    }
  }, [navigate]);

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
    if (searchParams.get('login') === 'true') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('login');
      setSearchParams(nextParams, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleCloseLoginModal}
      />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20business%20dashboard%20interface%20with%20clean%20minimal%20design%2C%20futuristic%20SEO%20analytics%20workspace%2C%20professional%20technology%20background%20with%20blue%20and%20purple%20gradient%20colors%2C%20sophisticated%20data%20visualization%20environment&width=1200&height=800&seq=7&orientation=landscape"
            alt="Marketing4Sight Dashboard Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80"></div>
        </div>

        {/* Top Right Login Button */}
        <div className="absolute top-6 right-6 z-20">
          <button
            onClick={() => setIsLoginModalOpen(true)}
            className="bg-white/10 backdrop-blur-md text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40"
          >
            Login
          </button>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={logo} alt="Marketing4Sight Logo" className="w-36 h-36 object-contain" />
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Marketing4Sight
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Transform marketing complexity into actionable strategies—monitor visibility, performance, and growth at your fingertips.
            </p>

            <div className="flex justify-center gap-4">
              <button className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap">
                Watch Demo
              </button>
              <Link
                to="/onboarding"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Start Onboarding
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Intelligent Strategy Engine for Marketing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Boost visibility  with data-driven insights, competitive intel, ROI strategies, seamless implementation, and recommendations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-flag-line',
                title: 'Goal Tracking',
                description: 'Set benchmarks, monitor progress, and achieve measurable business outcomes.'
              },
              {
                icon: 'ri-spy-line',
                title: 'Competitive Intelligence',
                description: 'Track competitors, identify keyword gaps, and discover untapped market opportunities.'
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Smart Recommendations',
                description: 'AI-powered strategy suggestions prioritized by effort and impact for maximum ROI.'
              },
              {
                icon: 'ri-dashboard-3-line',
                title: 'Real-time Monitoring',
                description: 'Monitor marketing performance across channels with live data updates and intelligent alerts for immediate action.'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'ROI Forecasting',
                description: 'Predict returns on marketing investments with scenario planning and cross-channel performance modeling.'
              },
              {
                icon: 'ri-robot-line',
                title: 'AI Strategy Assistant',
                description: 'Chat with your personal marketing strategist for instant insights, channel recommendations, and actionable growth advice.'
              }
            ].map((feature, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Plan and Implement Your Marketing Strategy?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of businesses using Marketing4Sight to drive growth and outperform competitors.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Move to Top
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="Marketing4Sight" className="w-12 h-12 object-contain" />
              <span className="text-lg font-semibold">Marketing4Sight</span>
            </div>
            <div className="text-sm text-gray-400">
              © {new Date().getFullYear()} Marketing4Sight
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
